import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FieldWrap, NativeSelect } from "@/components/Field";
import { OrderList } from "@/components/OrderList";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, money } from "@/lib/format";

const EMPTY = { name: "", date: "", end_date: "", location: "", fee_adult: "", fee_youth: "", fee_child: "", collection: "", destination: "", description: "", active: true };
const FEES = [["fee_adult", "Adult"], ["fee_youth", "Youth"], ["fee_child", "Child"]];
const withFees = (ev) => ({ ...ev, fee_adult: ev.fee_adult ?? ev.fee ?? "" });
const toBody = ({ name, date, end_date, location, description, active, collection, destination, ...v }) => ({
  name, date, end_date, location, description, active, collection: collection || "", destination: destination || "", ...Object.fromEntries(FEES.map(([k]) => [k, parseFloat(v[k]) || 0])),
});
const feeText = (ev) => FEES.filter(([k]) => ev[k] > 0).map(([k, l]) => `${l} ${money(ev[k])}`).join(", ");

function EventDialog({ open, event, onClose, onSaved }) {
  const { collections, destinations } = useApp().options;
  const [v, setV] = useState(EMPTY);
  useEffect(() => { if (open) { setV(event ? { ...EMPTY, ...withFees(event) } : EMPTY); } }, [open, event]);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (!v.name.trim()) { toast.error("Please enter the event name"); return; }
    try {
      const body = toBody(v);
      if (event) await api.put(`/events/${event.id}`, body); else await api.post("/events", body);
      toast.success("Event saved");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid="event-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{event ? "Edit event" : "New conference event"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <FieldWrap label="Event name" required><Input className="h-11" value={v.name} onChange={set("name")} data-testid="event-name-input" /></FieldWrap>
          <div className="grid grid-cols-2 gap-3">
            <FieldWrap label="Start date"><Input type="date" className="h-11" value={v.date} onChange={set("date")} data-testid="event-date-input" /></FieldWrap>
            <FieldWrap label="End date"><Input type="date" className="h-11" value={v.end_date} onChange={set("end_date")} data-testid="event-end-date-input" /></FieldWrap>
          </div>
          <FieldWrap label="Location"><Input className="h-11" value={v.location} onChange={set("location")} data-testid="event-location-input" /></FieldWrap>
          <div className="grid grid-cols-3 gap-3">
            {FEES.map(([k, l]) => (
              <FieldWrap key={k} label={`${l} fee ($)`}><Input type="number" min="0" step="0.01" className="h-11" value={v[k]} onChange={set(k)} data-testid={`event-${k.replace("_", "-")}-input`} /></FieldWrap>
            ))}
          </div>
          <FieldWrap label="Section" sub="Shows this event under General Council or MWJK. Not set = both."><NativeSelect value={v.destination || ""} onChange={(x) => setV({ ...v, destination: x })} placeholder="Not set" options={destinations.map((d) => d.name)} testid="event-destination-select" /></FieldWrap>
          <FieldWrap label="Collection (for Conference Summary)" sub="Paid registration fees are added to this collection's income."><NativeSelect value={v.collection || ""} onChange={(x) => setV({ ...v, collection: x })} placeholder="None" options={collections.map((c) => c.name)} testid="event-collection-select" /></FieldWrap>
          <FieldWrap label="Description"><Textarea rows={3} value={v.description} onChange={set("description")} data-testid="event-description-input" /></FieldWrap>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="event-save-btn">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminEvents() {
  const { events, reload } = useApp();
  const [dialog, setDialog] = useState({ open: false, event: null });
  const call = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const toggle = (ev, active) => call(() => api.put(`/events/${ev.id}`, toBody({ ...withFees(ev), active })), active ? "Registration opened" : "Registration closed");
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-on-bg-muted text-sm">Open events appear for church users to register members. Drag to reorder.</p>
        <Button onClick={() => setDialog({ open: true, event: null })} data-testid="add-event-btn"><Plus className="mr-2 h-4 w-4" /> Add event</Button>
      </div>
      <OrderList items={events} onSave={(ids) => call(() => api.put("/events/reorder", { ids }), "Order saved")} testid="event" empty="No conference events yet."
        renderItem={(ev) => (
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold text-slate-900">{ev.name}</div>
              <div className="text-sm text-slate-500">{[ev.date && fmtDate(ev.date), ev.location, feeText(withFees(ev))].filter(Boolean).join(" · ")}</div>
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
              Registration
              <OnOffSwitch checked={ev.active} onChange={(a) => toggle(ev, a)} testid={`event-active-${ev.id}`} />
            </label>
            <Button variant="ghost" size="icon" onClick={() => setDialog({ open: true, event: ev })} data-testid={`event-edit-${ev.id}`}><Pencil className="h-4 w-4" /></Button>
            <ConfirmDelete onConfirm={() => call(() => api.delete(`/events/${ev.id}`), "Event deleted")} title={`Delete ${ev.name}?`} message="All registrations for this event will also be deleted." testid={`event-delete-${ev.id}`} />
          </div>
        )} />
      <EventDialog open={dialog.open} event={dialog.event} onClose={() => setDialog({ open: false, event: null })} onSaved={() => { setDialog({ open: false, event: null }); reload(); }} />
    </div>
  );
}
